import { Close as CloseIcon, Launch as LaunchIcon, ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    alpha,
    TableContainer,
    Skeleton,
    Fade,
} from '@mui/material';
import ResponsiveTooltip from '../ResponsiveTooltip';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../context/AuthContext';
import { useIntegrationService } from '../../services/integration.service';
import { Integration } from '../../services/mocks/data/integrations';
import { formatCurrency } from '../../utils/formatters';
import { QUERY_KEYS } from '../../utils/queryKeys';
import { useNavigate } from 'react-router-dom';

interface IntegrationAssetsModalProps {
    open: boolean;
    onClose: () => void;
    integration: Integration;
}

const IntegrationAssetsModal: React.FC<IntegrationAssetsModalProps> = ({ open, onClose, integration }) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const integrationService = useIntegrationService();
    const theme = useTheme();
    const navigate = useNavigate();

    const { data: tokenAllocation, isLoading } = useQuery({
        queryKey: [QUERY_KEYS.INTEGRATIONS_TOKEN_ALLOCATION, integration.name],
        queryFn: () => integrationService.getTokenAllocation(integration.name),
        enabled: !!currentCtUser && open,
    });

    const handleNavigateToTransactions = (assetSymbol: string, accountName: string) => {
        onClose();
        navigate(`/transactions?account=${accountName}&asset=${assetSymbol}`);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { 
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    overflow: 'hidden'
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxHeight: { xs: '95vh', sm: '90vh' },
                    overflow: 'auto',
                    bgcolor: (theme) => theme.palette.background.paper,
                    color: (theme) => theme.palette.text.primary,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                    boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
            >
                <DialogTitle sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 1, sm: 2 },
                            flexGrow: 1 
                        }}>
                            <Avatar
                                src={'https://cointracking.info/' + integration.logoUri}
                                alt={integration.name}
                                sx={{ 
                                    width: { xs: 36, sm: 48 },
                                    height: { xs: 36, sm: 48 }
                                }}
                            />
                            <Typography variant="h5" component="div" sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                            }}>
                                {t('integrations.assetsModal.title', { platform: integration.platformDisplayName ?? integration.name })}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: { xs: 2, sm: 3, md: 4 } }}>
                    {isLoading ? (
                        <Fade in={isLoading} timeout={300}>
                            <Box>
                                <Box sx={{ mb: 2 }}>
                                    <TableContainer sx={{ px: 4 }}>
                                        <Table size="medium" sx={{
                                            minWidth: 750,
                                            tableLayout: 'fixed',
                                            borderCollapse: 'collapse',
                                            width: '100%',
                                            '& .MuiTableCell-root': {
                                                py: 1.5,
                                                borderBottom: '2px solid',
                                                borderBottomColor: `${theme.palette.divider}`,
                                                borderBottomOpacity: 0.6,
                                            },
                                            '& .MuiTableHead-root .MuiTableCell-root': {
                                                bgcolor: 'background.paper',
                                                fontWeight: 600,
                                                borderBottom: '2px solid',
                                                borderBottomColor: `${theme.palette.divider}`,
                                            },
                                        }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.asset')}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.quantity')}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.value')}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.allocation')}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[1, 2, 3, 4, 5, 6].map((rowIndex) => (
                                                    <TableRow key={`skeleton-row-${rowIndex}`}>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Skeleton variant="circular" width={24} height={24} 
                                                                        sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1) }} />
                                                                    <Box>
                                                                        <Skeleton width={80} height={20} 
                                                                            sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1) }} />
                                                                        <Skeleton width={40} height={14} 
                                                                            sx={{ 
                                                                                bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                                                mt: 0.5
                                                                            }} />
                                                                    </Box>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Skeleton width={60} height={20} 
                                                                sx={{ 
                                                                    bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                                    ml: 'auto'
                                                                }} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Skeleton width={70} height={20} 
                                                                sx={{ 
                                                                    bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                                    ml: 'auto'
                                                                }} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Skeleton width={50} height={20} 
                                                                sx={{ 
                                                                    bgcolor: alpha(theme.palette.text.secondary, 0.1),
                                                                    ml: 'auto' 
                                                                }} />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        </Fade>
                    ) : tokenAllocation && (
                        <Fade in={!isLoading} timeout={500}>
                            <Box>
                                {Object.entries(tokenAllocation)
                                    .filter(([_, assets]) => assets.length > 0)
                                    .map(([key, assets]) => (
                                        <Box key={key} sx={{ mb: 2 }}>
                                            <Box sx={{ mb: 1, px: 4 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                                    {key}
                                                </Typography>
                                            </Box>
                                            <TableContainer
                                                sx={{
                                                    overflow: 'auto',
                                                    px: { xs: 1, sm: 2, md: 4 },
                                                    '&::-webkit-scrollbar': {
                                                        height: 6,
                                                        width: '8px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        backgroundColor: alpha(theme.palette.divider, 0.6),
                                                        borderRadius: 3,
                                                    },
                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                        backgroundColor: theme.palette.action.hover,
                                                    },
                                                }}
                                            >
                                            <Table 
                                                size="medium"
                                                sx={{
                                                    minWidth: { xs: 600, sm: 600, md: 750 },
                                                    tableLayout: 'fixed',
                                                    borderCollapse: 'collapse',
                                                    width: '100%',
                                                    '& .MuiTableCell-root': {
                                                        py: 1.5,
                                                        px: { xs: 1, sm: 2 },
                                                        borderBottom: '2px solid',
                                                        borderBottomColor: `${theme.palette.divider}`,
                                                        borderBottomOpacity: 0.6,
                                                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    },
                                                    '& .MuiTypography-root': {
                                                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                                                    },
                                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                                        bgcolor: 'background.paper',
                                                        fontWeight: 600,
                                                        borderBottom: '2px solid',
                                                        borderBottomColor: `${theme.palette.divider}`,
                                                    },
                                                    '& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
                                                        borderBottom: 'none',
                                                    },
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: { xs: '40%', sm: '45%', md: '50%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.asset')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '20%', md: '20%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.quantity')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '20%', md: '15%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.value')}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '15%', md: '15%' } }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.assetsModal.table.allocation')}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {assets.map((asset) => (
                                                        <TableRow key={`${key}-${asset.coin}`}>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Box
                                                                        sx={{ 
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1
                                                                        }}
                                                                    >
                                                                        <Avatar
                                                                            src={`https://cointracking.info/img/coins/${asset.image}`}
                                                                            alt={asset.coin || ''}
                                                                            sx={{ width: 24, height: 24 }}
                                                                        />
                                                                        <Box>
                                                                            <Typography
                                                                                sx={{ 
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    fontWeight: 'bold',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                                                    maxWidth: { xs: '120px', sm: '150px', md: '200px', lg: '250px' }
                                                                                }}
                                                                            >
                                                                                <ResponsiveTooltip title={asset.name || asset.coin}>
                                                                                    <span>{asset.name || asset.coin}</span>
                                                                                </ResponsiveTooltip>
                                                                                {asset.quantity_mismatch ? (
                                                                                    <>
                                                                                        <ResponsiveTooltip
                                                                                            title={
                                                                                                <Box sx={{ p: 1.5 }}>
                                                                                                    <Typography 
                                                                                                        variant="body2" 
                                                                                                        sx={{ 
                                                                                                            fontWeight: 'bold', 
                                                                                                            fontSize: '0.9rem',
                                                                                                            whiteSpace: 'nowrap'
                                                                                                        }}
                                                                                                    >
                                                                                                        {t('dashboard.assetDistribution.balanceDiscrepancy.tooltip', {
                                                                                                            balance: asset.imported_quantity.toFixed(8)
                                                                                                        })}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            }
                                                                                            arrow
                                                                                            placement="top"
                                                                                            componentsProps={{
                                                                                                tooltip: {
                                                                                                    sx: {
                                                                                                        p: 0,
                                                                                                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                                        backdropFilter: 'blur(5px)',
                                                                                                        color: (theme) => theme.palette.text.primary,
                                                                                                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                                                                        border: '1px solid',
                                                                                                        borderColor: (theme) => theme.palette.divider,
                                                                                                        borderRadius: 1,
                                                                                                        fontSize: '0.9rem',
                                                                                                        fontWeight: 'bold',
                                                                                                        '& .MuiTooltip-arrow': {
                                                                                                            color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                                        }
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                color="warning"
                                                                                                sx={{
                                                                                                    padding: '2px',
                                                                                                    ml: 0.5,
                                                                                                }}
                                                                                            >
                                                                                                <ReportProblemIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </ResponsiveTooltip>
                                                                                        <ResponsiveTooltip
                                                                                            title={
                                                                                                <Box sx={{ p: 1.5 }}>
                                                                                                    <Typography 
                                                                                                        variant="body2" 
                                                                                                        sx={{ 
                                                                                                            fontWeight: 'bold', 
                                                                                                            fontSize: '0.9rem',
                                                                                                            whiteSpace: 'nowrap'
                                                                                                        }}
                                                                                                    >
                                                                                                        {t('dashboard.assetDistribution.balanceDiscrepancy.viewTransactions')}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            }
                                                                                            arrow
                                                                                            placement="top"
                                                                                            componentsProps={{
                                                                                                tooltip: {
                                                                                                    sx: {
                                                                                                        p: 0,
                                                                                                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                                        backdropFilter: 'blur(5px)',
                                                                                                        color: (theme) => theme.palette.text.primary,
                                                                                                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                                                                        border: '1px solid',
                                                                                                        borderColor: (theme) => theme.palette.divider,
                                                                                                        borderRadius: 1,
                                                                                                        fontSize: '0.9rem',
                                                                                                        fontWeight: 'bold',
                                                                                                        '& .MuiTooltip-arrow': {
                                                                                                            color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                                        }
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                color="primary"
                                                                                                onClick={() => handleNavigateToTransactions(asset.coin, key)}
                                                                                                sx={{
                                                                                                    padding: '2px',
                                                                                                    ml: 0.5,
                                                                                                    '&:hover': {
                                                                                                        backgroundColor: theme.palette.primary.light + '20',
                                                                                                    },
                                                                                                }}
                                                                                            >
                                                                                                <LaunchIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </ResponsiveTooltip>
                                                                                    </>
                                                                                ) : (
                                                                                    ''
                                                                                )}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {asset.symbol && asset.symbol.trim() ? (
                                                                                    <span
                                                                                        dangerouslySetInnerHTML={{
                                                                                            __html: asset.symbol,
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    asset.coin
                                                                                )}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <ResponsiveTooltip title={asset.quantity.toFixed(8)}>
                                                                    <Typography sx={{ 
                                                                        fontWeight: 'bold',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {asset.quantity.toFixed(8)}
                                                                    </Typography>
                                                                </ResponsiveTooltip>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <ResponsiveTooltip title={formatCurrency(asset.value)}>
                                                                    <Typography sx={{ 
                                                                        fontWeight: 'bold',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {formatCurrency(asset.value)}
                                                                    </Typography>
                                                                </ResponsiveTooltip>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography sx={{ 
                                                                    fontWeight: 'bold',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {asset.allocation.toFixed(2)}%
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                            </TableContainer>
                                        </Box>
                                    ))}
                            </Box>
                        </Fade>
                    )}
                </DialogContent>
            </Box>
        </Dialog>
    );
};

export default IntegrationAssetsModal;
